import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw, 
  Search,
  Folder,
  GraduationCap,
  TrendingUp,
  Activity,
  BookOpen,
  Users,
  Package,
  Filter,
  Eye,
  EyeOff,
  Edit3,
  MoreHorizontal,
  ChevronDown,
  Download,
  Settings
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

interface CourseFamily {
  id: string;
  name: string;
  description: string | null;
  requirements: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  course_count?: number;
}

interface CourseFamilySummary {
  totalFamilies: number;
  totalCourses: number;
  averageCoursesPerFamily: number;
  activeFamilies: number;
}

const CourseFamilies = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [families, setFamilies] = useState<CourseFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [summaryData, setSummaryData] = useState<CourseFamilySummary>({
    totalFamilies: 0,
    totalCourses: 0,
    averageCoursesPerFamily: 0,
    activeFamilies: 0
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showEmptyFamilies, setShowEmptyFamilies] = useState(true);
  const [showInactiveFamilies, setShowInactiveFamilies] = useState(true);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [currentFamily, setCurrentFamily] = useState<CourseFamily | null>(null);
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRequirements, setFormRequirements] = useState('');

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    setLoading(true);
    try {
      // Buscar famílias de cursos e contar quantos cursos estão associados a cada uma
      const { data: familiesData, error: familiesError } = await supabase
        .from('course_families')
        .select('*')
        .order('name');

      if (familiesError) throw familiesError;

      // Buscar contagem de cursos para cada família
      const familiesWithCounts = await Promise.all(
        familiesData.map(async (family) => {
          const { count, error: countError } = await supabase
            .from('courses')
            .select('*', { count: 'exact', head: true })
            .eq('course_family_id', family.id);
          
          if (countError) {
            console.error('Erro ao contar cursos:', countError);
            return { ...family, course_count: 0 };
          }
          
          return { ...family, course_count: count || 0 };
        })
      );
      
      setFamilies(familiesWithCounts);
      
      // Calcular dados de resumo
      const totalCourses = familiesWithCounts.reduce((sum, family) => sum + (family.course_count || 0), 0);
      const averageCoursesPerFamily = familiesWithCounts.length > 0 ? totalCourses / familiesWithCounts.length : 0;
      const activeFamilies = familiesWithCounts.filter(family => family.is_active).length;
      
      setSummaryData({
        totalFamilies: familiesWithCounts.length,
        totalCourses,
        averageCoursesPerFamily: Math.round(averageCoursesPerFamily * 10) / 10,
        activeFamilies
      });
    } catch (error) {
      console.error('Erro ao buscar famílias de cursos:', error);
      toast.error('Não foi possível carregar as famílias de cursos');
    } finally {
      setLoading(false);
    }
  };

    const getFilteredFamilies = () => {
    let filtered = families;

    // Filtro por termo de busca
    if (searchTerm.trim()) {
      filtered = filtered.filter(family => 
      family.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (family.description && family.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    }

    // Filtro para mostrar/esconder famílias vazias
    if (!showEmptyFamilies) {
      filtered = filtered.filter(family => (family.course_count || 0) > 0);
    }

    // Filtro para mostrar/esconder famílias inativas
    if (!showInactiveFamilies) {
      filtered = filtered.filter(family => family.is_active);
    }

    return filtered;
  };

  const openAddDialog = () => {
    setFormName('');
    setFormDescription('');
    setFormRequirements('');
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (family: CourseFamily) => {
    setCurrentFamily(family);
    setFormName(family.name);
    setFormDescription(family.description || '');
    setFormRequirements(family.requirements || '');
    setIsEditDialogOpen(true);
  };

  const openStatusDialog = (family: CourseFamily) => {
    setCurrentFamily(family);
    setIsStatusDialogOpen(true);
  };

  const handleAddFamily = async () => {
    if (!formName.trim()) {
      toast.error('O nome da família é obrigatório');
      return;
    }

    if (!user?.id) {
      toast.error('Usuário não autenticado');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('course_families')
        .insert([
          { 
            name: formName, 
            description: formDescription || null,
            requirements: formRequirements || null,
            is_active: true,
            created_by: user.id,
            updated_by: user.id
          }
        ])
        .select();

      if (error) throw error;

      toast.success('Família de cursos criada com sucesso');
      setIsAddDialogOpen(false);
      fetchFamilies();
    } catch (error) {
      console.error('Erro ao criar família de cursos:', error);
      toast.error('Não foi possível criar a família de cursos');
    }
  };

  const handleEditFamily = async () => {
    if (!currentFamily || !formName.trim()) {
      toast.error('O nome da família é obrigatório');
      return;
    }

    if (!user?.id) {
      toast.error('Usuário não autenticado');
      return;
    }

    try {
      const { error } = await supabase
        .from('course_families')
        .update({ 
          name: formName, 
          description: formDescription || null,
          requirements: formRequirements || null,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', currentFamily.id);

      if (error) throw error;

      toast.success('Família de cursos atualizada com sucesso');
      setIsEditDialogOpen(false);
      fetchFamilies();
    } catch (error) {
      console.error('Erro ao atualizar família de cursos:', error);
      toast.error('Não foi possível atualizar a família de cursos');
    }
  };

  const handleToggleStatus = async () => {
    if (!currentFamily || !user?.id) return;

    const newStatus = !currentFamily.is_active;
    const action = newStatus ? 'ativada' : 'inativada';

    try {
      const { error } = await supabase
        .from('course_families')
        .update({ 
          is_active: newStatus,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', currentFamily.id);

      if (error) throw error;

      toast.success(`Família de cursos ${action} com sucesso`);
      setIsStatusDialogOpen(false);
      fetchFamilies();
    } catch (error) {
      console.error('Erro ao alterar status da família de cursos:', error);
      toast.error('Não foi possível alterar o status da família de cursos');
    }
  };

  const filteredFamilies = getFilteredFamilies();

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Famílias de Cursos</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Header with Gradient Background */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <GraduationCap className="h-6 w-6 text-white" />
                  </div>
        <div>
                    <h1 className="text-2xl font-extrabold text-white">Famílias de Cursos</h1>
                    <p className="text-blue-100 text-base pb-2">
            Gerencie as categorias e famílias de cursos disponíveis
          </p>
        </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => fetchFamilies()}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
                <Button 
                  size="default" 
                  onClick={openAddDialog}
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Família
          </Button>
        </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
          {/* Enhanced Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                      <Folder className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Total de Famílias</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.totalFamilies}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Categorias ativas
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-blue-600 font-extrabold">+5%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 rounded-md">
                      <BookOpen className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Total de Cursos</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.totalCourses}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Em todas as famílias
                  </div>
                  <div className="flex items-center gap-1">
                    <Activity className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600 font-extrabold">+8%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-100 rounded-md">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Média por Família</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.averageCoursesPerFamily}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Cursos por categoria
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-purple-500" />
                    <span className="text-xs text-purple-600 font-extrabold">+2%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-orange-100 rounded-md">
                      <Package className="h-4 w-4 text-orange-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Famílias Ativas</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.activeFamilies}
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress 
                    value={summaryData.totalFamilies > 0 ? (summaryData.activeFamilies / summaryData.totalFamilies) * 100 : 0} 
                    className="h-1.5"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {summaryData.totalFamilies > 0 
                        ? `${Math.round((summaryData.activeFamilies / summaryData.totalFamilies) * 100)}% do total` 
                        : 'Nenhuma família'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Activity className="h-3 w-3 text-orange-500" />
                      <span className="text-xs text-orange-600 font-extrabold">100%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
      </div>
      
          {/* Enhanced Main Content */}
          <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              {/* Enhanced Filter Section */}
              <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative min-w-0 flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
              <Input
                      placeholder="Pesquisar famílias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-10 px-4 border-gray-200 hover:bg-gray-50">
                        <Filter className="h-4 w-4 mr-2" />
                        Filtros
                        {(!showEmptyFamilies || !showInactiveFamilies) && (
                          <Badge variant="secondary" className="ml-2 h-4 w-4 p-0 text-xs">
                            {(!showEmptyFamilies ? 1 : 0) + (!showInactiveFamilies ? 1 : 0)}
                          </Badge>
                        )}
                        <ChevronDown className="h-3 w-3 ml-2 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-6" align="end">
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-extrabold text-gray-900 mb-1">Filtros Avançados</h4>
                          <p className="text-sm text-gray-500">Personalize a visualização das famílias</p>
                        </div>
                        
                        <Separator />
                        
                                                <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <label className="text-sm font-extrabold text-gray-700">
                                Mostrar famílias vazias
                              </label>
                              <p className="text-xs text-gray-500">
                                Incluir famílias sem cursos associados
                              </p>
                            </div>
                            <Checkbox 
                              checked={showEmptyFamilies} 
                              onCheckedChange={(checked) => setShowEmptyFamilies(!!checked)} 
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <label className="text-sm font-extrabold text-gray-700">
                                Mostrar famílias inativas
                              </label>
                              <p className="text-xs text-gray-500">
                                Incluir famílias desativadas na listagem
                              </p>
                            </div>
                            <Checkbox 
                              checked={showInactiveFamilies} 
                              onCheckedChange={(checked) => setShowInactiveFamilies(!!checked)} 
              />
            </div>
                        </div>
                        
                        <div className="flex justify-between pt-4 border-t">
                                                    <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                setSearchTerm('');
                              setShowEmptyFamilies(true);
                              setShowInactiveFamilies(true);
                            }}
                          >
                            Limpar Filtros
                          </Button>
                          <Button size="sm" onClick={() => setIsFilterOpen(false)}>
                            Aplicar
              </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
            </div>
          </div>

              {/* Content Area */}
          {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="space-y-4 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent mx-auto"></div>
                    <p className="text-gray-500">Carregando famílias de cursos...</p>
                  </div>
            </div>
              ) : filteredFamilies.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-gray-200">
              <Table>
                    <TableHeader className="bg-gray-50/80">
                                        <TableRow>
                        <TableHead className="font-extrabold text-gray-900 w-full">Família</TableHead>
                        <TableHead className="font-extrabold text-gray-900 whitespace-nowrap">Cursos</TableHead>
                        <TableHead className="font-extrabold text-gray-900 whitespace-nowrap">Status</TableHead>
                        <TableHead className="text-right font-extrabold text-gray-900 whitespace-nowrap">Ações</TableHead>
                      </TableRow>
                </TableHeader>
                <TableBody>
                                            {filteredFamilies.map((family) => (
                        <TableRow key={family.id} className="group hover:bg-gray-50/50 transition-colors">
                          <TableCell className="w-full pr-8">
                            <div className="space-y-1">
                              <div className="font-extrabold text-gray-900 leading-tight">
                                {family.name}
                              </div>
                              {family.description ? (
                                <div className="text-sm text-gray-600 leading-relaxed">
                                  {family.description}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-400 italic">
                                  Sem descrição
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {(family.course_count || 0) > 0 ? (
                              <Badge 
                                variant="outline" 
                                className="bg-blue-50 text-blue-700 border-blue-200"
                              >
                                <BookOpen className="h-3 w-3 mr-1" />
                                {family.course_count} cursos
                              </Badge>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Badge 
                              variant="outline" 
                              className={`${
                                family.is_active 
                                  ? 'bg-green-50 text-green-700 border-green-200' 
                                  : 'bg-gray-50 text-gray-500 border-gray-200'
                              }`}
                            >
                              <div className={`w-2 h-2 rounded-full mr-2 ${
                                family.is_active ? 'bg-green-500' : 'bg-gray-400'
                              }`} />
                              {family.is_active ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditDialog(family)}
                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                                title="Editar família"
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openStatusDialog(family)}
                                className={`h-8 w-8 p-0 ${
                                  family.is_active 
                                    ? 'hover:bg-yellow-50 hover:text-yellow-600' 
                                    : 'hover:bg-green-50 hover:text-green-600'
                                }`}
                                title={family.is_active ? 'Inativar família' : 'Ativar família'}
                              >
                                {family.is_active ? (
                                  <Eye className="h-4 w-4" />
                                ) : (
                                  <EyeOff className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </div>
              ) : (
                                  <div className="text-center py-20">
                  <div className="space-y-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                      <GraduationCap className="h-12 w-12 text-blue-500" />
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-xl font-extrabold text-gray-900">
                        {searchTerm ? 'Nenhuma família encontrada' : 'Nenhuma família de cursos'}
                      </h3>
                      <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                        {searchTerm 
                          ? 'Não encontramos famílias que correspondam aos seus critérios de busca. Tente alterar os filtros.'
                          : 'Para começar, crie famílias de cursos para organizar e categorizar seus diferentes tipos de cursos de forma eficiente.'
                        }
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button onClick={openAddDialog} size="lg">
                        <Plus className="h-5 w-5 mr-2" />
                        Criar Primeira Família
                      </Button>
                      {searchTerm && (
                        <Button 
                          variant="outline" 
                          size="lg"
                          onClick={() => {
                            setSearchTerm('');
                            setShowEmptyFamilies(true);
                            setShowInactiveFamilies(true);
                          }}
                        >
                          <RefreshCw className="h-5 w-5 mr-2" />
                          Limpar Filtros
                        </Button>
                      )}
                    </div>
                  </div>
            </div>
          )}
        </CardContent>
      </Card>
        </div>
        
        {/* Bottom Spacing */}
        <div className="h-16" />
      
        {/* Enhanced Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-white/50 to-teal-50/50 rounded-lg" />
            <div className="relative">
              <DialogHeader className="space-y-4 pb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Plus className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl font-extrabold text-gray-900">
                      Nova Família de Cursos
                    </DialogTitle>
                    <DialogDescription className="text-gray-600 mt-1">
                      Crie uma nova categoria para organizar seus cursos de forma eficiente
            </DialogDescription>
                  </div>
                </div>
          </DialogHeader>
              
              <div className="space-y-6 py-4">
              <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-extrabold text-gray-700">
                    Nome da Família *
                  </Label>
                <Input 
                  id="name" 
                    placeholder="Ex: Tecnologia, Gestão, Marketing..." 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                    className="bg-white/80 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    required
                />
              </div>
                
              <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm font-extrabold text-gray-700">
                    Descrição
                  </Label>
                <Textarea 
                  id="description" 
                    placeholder="Descreva o propósito e escopo desta família de cursos..." 
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                    className="bg-white/80 border-gray-200 focus:border-blue-500 focus:ring-blue-500 min-h-[80px]"
                    rows={3}
                />
              </div>
                
              <div className="space-y-2">
                  <Label htmlFor="requirements" className="text-sm font-extrabold text-gray-700">
                    Requisitos Gerais
                  </Label>
                <Textarea 
                  id="requirements" 
                    placeholder="Defina requisitos comuns para todos os cursos desta família..." 
                  value={formRequirements}
                  onChange={(e) => setFormRequirements(e.target.value)}
                    className="bg-white/80 border-gray-200 focus:border-blue-500 focus:ring-blue-500 min-h-[80px]"
                    rows={3}
                />
              </div>
            </div>
              
              <DialogFooter className="pt-6 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setIsAddDialogOpen(false)}
                  className="border-gray-200 hover:bg-gray-50"
                >
              Cancelar
            </Button>
                <Button 
                  onClick={handleAddFamily}
                  disabled={!formName.trim()}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Família
                </Button>
          </DialogFooter>
            </div>
        </DialogContent>
      </Dialog>

        {/* Enhanced Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px] bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/50 to-indigo-50/50 rounded-lg" />
            <div className="relative">
              <DialogHeader className="space-y-4 pb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Edit3 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl font-extrabold text-gray-900">
                      Editar Família de Cursos
                    </DialogTitle>
                    <DialogDescription className="text-gray-600 mt-1">
                      Atualize as informações da família "{currentFamily?.name}"
            </DialogDescription>
                  </div>
                </div>
          </DialogHeader>
              
              <div className="space-y-6 py-4">
              <div className="space-y-2">
                  <Label htmlFor="edit-name" className="text-sm font-extrabold text-gray-700">
                    Nome da Família *
                  </Label>
                <Input 
                  id="edit-name" 
                    placeholder="Ex: Tecnologia, Gestão, Marketing..." 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                    className="bg-white/80 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    required
                />
              </div>
                
              <div className="space-y-2">
                  <Label htmlFor="edit-description" className="text-sm font-extrabold text-gray-700">
                    Descrição
                  </Label>
                <Textarea 
                  id="edit-description" 
                    placeholder="Descreva o propósito e escopo desta família de cursos..." 
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                    className="bg-white/80 border-gray-200 focus:border-blue-500 focus:ring-blue-500 min-h-[80px]"
                    rows={3}
                />
              </div>
                
              <div className="space-y-2">
                  <Label htmlFor="edit-requirements" className="text-sm font-extrabold text-gray-700">
                    Requisitos Gerais
                  </Label>
                <Textarea 
                  id="edit-requirements" 
                    placeholder="Defina requisitos comuns para todos os cursos desta família..." 
                  value={formRequirements}
                  onChange={(e) => setFormRequirements(e.target.value)}
                    className="bg-white/80 border-gray-200 focus:border-blue-500 focus:ring-blue-500 min-h-[80px]"
                    rows={3}
                  />
                </div>
                
                {currentFamily?.course_count && currentFamily.course_count > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-extrabold text-blue-900">
                        Cursos Associados
                      </span>
                    </div>
                    <p className="text-sm text-blue-700">
                      Esta família possui {currentFamily.course_count} curso(s) associado(s). 
                      As alterações feitas aqui não afetarão os cursos existentes.
                    </p>
                  </div>
                )}
              </div>
              
              <DialogFooter className="pt-6 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditDialogOpen(false)}
                  className="border-gray-200 hover:bg-gray-50"
                >
              Cancelar
            </Button>
                <Button 
                  onClick={handleEditFamily}
                  disabled={!formName.trim()}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Salvar Alterações
                </Button>
          </DialogFooter>
            </div>
        </DialogContent>
      </Dialog>

                {/* Enhanced Status Change Confirmation Dialog */}
        <AlertDialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
          <AlertDialogContent className="sm:max-w-[500px] bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
            <div className={`absolute inset-0 ${
              currentFamily?.is_active 
                ? 'bg-gradient-to-br from-yellow-50/50 via-white/50 to-orange-50/50' 
                : 'bg-gradient-to-br from-green-50/50 via-white/50 to-emerald-50/50'
            } rounded-lg`} />
            <div className="relative">
              <AlertDialogHeader className="space-y-4 pb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    currentFamily?.is_active 
                      ? 'bg-yellow-100' 
                      : 'bg-green-100'
                  }`}>
                    {currentFamily?.is_active ? (
                      <EyeOff className="h-5 w-5 text-yellow-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-green-600" />
                    )}
                  </div>
                  <div>
                    <AlertDialogTitle className="text-xl font-extrabold text-gray-900">
                      {currentFamily?.is_active ? 'Inativar' : 'Ativar'} Família de Cursos
                    </AlertDialogTitle>
                  </div>
                </div>
                
                <AlertDialogDescription className="text-gray-600 leading-relaxed">
                  Você está prestes a{' '}
                  <span className="font-extrabold">
                    {currentFamily?.is_active ? 'inativar' : 'ativar'}
                  </span>{' '}
                  a família de cursos{' '}
                  <span className="font-extrabold text-gray-900">"{currentFamily?.name}"</span>.
                  
                  <div className={`mt-4 p-4 border rounded-lg ${
                    currentFamily?.is_active 
                      ? 'bg-yellow-50 border-yellow-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-1 rounded ${
                        currentFamily?.is_active 
                          ? 'bg-yellow-100' 
                          : 'bg-green-100'
                      }`}>
                        <GraduationCap className={`h-4 w-4 ${
                          currentFamily?.is_active 
                            ? 'text-yellow-600' 
                            : 'text-green-600'
                        }`} />
                      </div>
                      <div>
                        <p className={`font-extrabold mb-1 ${
                          currentFamily?.is_active 
                            ? 'text-yellow-900' 
                            : 'text-green-900'
                        }`}>
                          {currentFamily?.is_active 
                            ? 'Inativando a família' 
                            : 'Ativando a família'
                          }
                        </p>
                        <p className={`text-sm ${
                          currentFamily?.is_active 
                            ? 'text-yellow-700' 
                            : 'text-green-700'
                        }`}>
                          {currentFamily?.is_active 
                            ? 'A família ficará oculta por padrão e não poderá ser usada para novos cursos.' 
                            : 'A família voltará a aparecer nas listagens e poderá ser usada para novos cursos.'
                          }
                          {currentFamily?.course_count && currentFamily.course_count > 0 && (
                            <span className="block mt-1">
                              Os {currentFamily.course_count} curso(s) associados não serão afetados.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              
              <AlertDialogFooter className="pt-6 border-t border-gray-100">
                <AlertDialogCancel className="border-gray-200 hover:bg-gray-50">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleToggleStatus}
                  className={`transition-all duration-300 ${
                    currentFamily?.is_active 
                      ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white' 
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                  }`}
                >
                  {currentFamily?.is_active ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Inativar Família
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Ativar Família
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
    </div>
    </>
  );
};

export default CourseFamilies; 